import { Button, type DialogComponentProps } from '@brightdrop/bd-ui';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useFormat } from '@brightdrop/localization-core';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Path, useLocation, useNavigate } from 'react-router-dom';

import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { LOCALIZATION_FEATURE_FLAG } from '~/common/models/featureFlags.model';
import { AppRoutePaths } from '~/common/models/route.model';
import { formatCountDownFromMilliseconds } from '~/common/utils/date-time.utils';
import {
  InactivityStorageKeys,
  modalCountdownTimeout,
} from '~/hooks/useInactivityTracker/InactivityTracker.constants';
import {
  getInitialCountdown,
  readLastActivity,
} from '~/hooks/useInactivityTracker/InactivityTracker.utils';
import { buildRedirectPathQuery } from '~/utils/auth/location.utils';

import useStyles from './InactivityUserCountdownModal.styles';

export type InactiveUserCountdownModalProps<T = never> =
  DialogComponentProps<T> & {
    modalTimeout?: number;
    idleTimeout?: number;
  };

const InactiveUserCountdownModal = ({
  onCloseModal,
  modalTimeout = modalCountdownTimeout,
  idleTimeout = 0,
}: InactiveUserCountdownModalProps) => {
  const { classes } = useStyles();
  // Subscribe to i18n so the imperative `translateText(...)` calls below re-render on locale change.
  useTranslation(['common']);
  const { getFlag } = useFeatureFlags();
  const format = useFormat();
  // Derive the initial countdown from the shared cross-tab activity timestamp
  // rather than always starting a fresh countdown. This keeps every tab's
  // countdown in sync and ensures a stale tab that has already exceeded the full
  // session timeout (e.g. reopened after being idle for a long time) logs out
  // immediately instead of granting another full countdown.
  const [timeRemaining, setTimeRemaining] = useState<number>(() =>
    getInitialCountdown(idleTimeout, modalTimeout, readLastActivity())
  );
  const location = useLocation();
  const navigate = useNavigate();
  // Initialize from the current location so the redirect path is available even
  // when the modal mounts already expired (timeRemaining === 0) and logs out
  // immediately, e.g. a stale tab brought back to the foreground.
  const [redirectQuery, setRedirectQuery] = useState<URLSearchParams>(() =>
    buildRedirectPathQuery(location)
  );
  // Guards against firing logout more than once (the countdown effect re-runs
  // whenever handleLogout is recreated, e.g. when redirectQuery updates).
  const hasLoggedOut = useRef(false);
  const handleLogout = useCallback(
    (isInactivityLogout = false) => {
      const path: Path = {
        pathname: AppRoutePaths.LOGOUT,
        search: '',
        hash: '',
      };
      //Only want to retain the last visited page upon logout due to inactivity
      //Else, treat the logout as the same when a user clicks on sign out from the header
      if (isInactivityLogout && redirectQuery?.size) {
        sessionStorage.setItem(InactivityStorageKeys.userSignedOut, 'true');
        path.search = redirectQuery.toString();
      }
      navigate(path);
    },
    [navigate, redirectQuery]
  );

  const handleNoButtonClick = () => {
    onCloseModal(false);
    //Note: the application detects when users are in an unauthenticated state,
    //so no need to emit an event for normal logout
    handleLogout(false);
  };

  const handleYesButtonClick = () => {
    onCloseModal(true);
  };

  useEffect(() => {
    //Sign out User based on inactivity in other window
    const handleStorageEvent = (event: StorageEvent) => {
      if (
        event.key === InactivityStorageKeys.userSignedOut &&
        event.newValue !== null &&
        !hasLoggedOut.current
      ) {
        //location seems to be preserved when one tab is logged out, which causes the other tabs to log out due to
        //auth token being stored in localStorage
        //Guard so multiple userSignedOut broadcasts (or a race with the countdown
        //expiry) can't trigger navigate() more than once per mount.
        hasLoggedOut.current = true;
        handleLogout(true);
      }
    };
    window.addEventListener('storage', handleStorageEvent);
    return () => window.removeEventListener('storage', handleStorageEvent);
  }, [handleLogout]);

  useEffect(() => {
    if (location) {
      setRedirectQuery(buildRedirectPathQuery(location));
    }
  }, [location]);

  useEffect(() => {
    if (timeRemaining > 0) {
      const timerId = setInterval(() => {
        //Clamp at 0 so a non-round starting value can't tick into a negative
        //countdown before the logout branch runs.
        setTimeRemaining((prevState) => Math.max(0, prevState - 1000));
      }, 1000);
      return () => clearInterval(timerId);
    }
    if (hasLoggedOut.current) {
      return;
    }
    hasLoggedOut.current = true;
    //Emit event to signal other tabs to sign out. Best-effort: storage can be
    //unavailable, and it must never block the logout navigation below.
    try {
      localStorage.setItem(InactivityStorageKeys.userSignedOut, 'true');
      localStorage.removeItem(InactivityStorageKeys.userSignedOut);
    } catch {
      // Ignore storage failures so logout still proceeds.
    }
    handleLogout(true);
  }, [handleLogout, timeRemaining]);

  // With the flag on the countdown reads off a clock face in the reader's own digits and field
  // separator; off, it keeps the legacy Luxon `m:ss` so the message is unchanged until rollout.
  const countdown = getFlag(LOCALIZATION_FEATURE_FLAG)
    ? format.clockDuration(timeRemaining / 1000)
    : formatCountDownFromMilliseconds(timeRemaining);

  return (
    <>
      <Typography
        variant="body_regular"
        className={classes.InactiveUserTimerMessage}
      >
        {translateText('common:inactivity:modal:timerMessage', {
          countdown,
        })}
      </Typography>
      <Box display="flex" flexDirection="column">
        <div className={classes.buttonContainer}>
          <Button
            onClick={handleNoButtonClick}
            variant={'tertiary'}
            data-testid="inactive-user-countdown-modal-no-button"
          >
            {translateText('common:inactivity:modal:signOut')}
          </Button>
          <Button
            onClick={handleYesButtonClick}
            data-testid="inactive-user-countdown-modal-yes-button"
          >
            {translateText('common:inactivity:modal:staySignedIn')}
          </Button>
        </div>
      </Box>
    </>
  );
};

export default InactiveUserCountdownModal;
